import { z } from '@customerio/hydra-validation';
import type { Shortcut as ApiShortcut } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * A profile shortcut (Ember `profile-shortcut`): a link or a webhook built from a person's
 * attributes. The workspace list holds the Liquid templates as written; a person's profile reads the
 * same shortcuts rendered for them (`customerKeys.profileShortcuts`), with any `render_errors`.
 */
export type ProfileShortcut = ApiShortcut;

// The spec types `render_errors` as a required list. Services sent `null` for a shortcut without
// errors until customerio/services#29440, so the read still turns a `null` into an empty list.
const ProfileShortcutFromApi = readSchema<ProfileShortcut>(
  z.looseObject({
    render_errors: z
      .array(z.string())
      .nullish()
      .transform((errors) => errors ?? []),
  }),
);

/** @public */
export const profileShortcut = defineEntity<ProfileShortcut>({
  name: 'profile-shortcut',
  scope: 'workspace',
  pk: (record) => record.id,
  schema: { read: ProfileShortcutFromApi },
});

/** Each shortcut a list read returned, with `render_errors` always a list. */
export function parseProfileShortcuts(raw: readonly unknown[]): ProfileShortcut[] {
  return raw.map((shortcut) => profileShortcut.parse(shortcut));
}

export const profileShortcutKeys = {
  ...profileShortcut.keys,
  /** Every shortcut in the workspace, unrendered. */
  workspaceList: (workspaceId: number) => [...profileShortcut.keys.lists(workspaceId), 'all'] as const,
};
